package com.elan.edubridge.shared.data.datasource

import com.elan.edubridge.shared.domain.model.AcademyMembership
import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.AuthResult
import com.elan.edubridge.shared.domain.model.UserAccount
import com.elan.edubridge.shared.domain.model.ProfileUpdateResult
import com.elan.edubridge.shared.domain.model.AccountDeletionState
import com.elan.edubridge.shared.domain.model.CommandFailure

/**
 * 원격 경계. 인증 공급자와 멤버십 저장소에 대한 접근만 담당한다.
 * 활성 학원 선택 같은 클라이언트 로컬 상태는 여기 두지 않는다.
 *
 * 실패 규약은 [AuthResult.errorMessage]다 — 복구 가능한 실패에 예외를 던지지 않는다.
 * 경계 메서드에 기본 인자를 두지 않는다(ObjC 헤더에 노출되지 않는다).
 */
interface AccountDataSource {
    suspend fun login(email: String, password: String): AuthResult
    suspend fun register(
        email: String,
        password: String,
        name: String,
        birthDate: String,
        ageGroup: AgeGroup,
    ): AuthResult

    /**
     * Google 계정으로 로그인한다.
     *
     * 플랫폼마다 토큰을 얻는 시점이 다르므로 [idToken]으로 그 차이를 흡수한다 — shared는
     * 어느 쪽 SDK도 알지 않는다.
     * - 웹: 팝업(`signInWithPopup`)이 토큰 교환까지 스스로 끝내므로 `null`을 넘긴다.
     * - Android/iOS: 플랫폼 SDK(Credential Manager 등)로 **먼저** 받아 둔 Google ID 토큰을 넘긴다.
     *   토큰 없이 부르면 로그인 자체가 불가능하므로 [AuthResult.errorMessage]로 실패를 돌려준다.
     *
     * [registrationName]·[birthDate]는 회원가입 화면에서만 함께 넘긴다. 프로필이 없는 Google
     * 계정은 두 값이 모두 유효할 때만 새 사용자 문서를 만들며, 기존 프로필 로그인에서는 둘 다
     * null이어야 하고 어떤 프로필 값도 덮어쓰지 않는다.
     *
     * 사용자가 스스로 취소한 경우(팝업 닫기·계정 선택 취소)는 오류가 아니므로 `account`도
     * `errorMessage`도 없는 빈 [AuthResult]를 돌려준다 — 호출부가 오류 배너를 띄우지 않는다.
     */
    suspend fun signInWithGoogle(
        idToken: String?,
        registrationName: String?,
        birthDate: String?,
    ): AuthResult

    /**
     * 영속 인증 상태 복원이 끝날 때까지 기다린다. **첫 렌더 이전에 반드시 1회.**
     *
     * Firebase 웹 SDK는 저장된 세션을 비동기로 복원하므로 이것 없이 [currentAccount]를 부르면
     * 첫 렌더에서 null이 나와 새로고침마다 로그인 화면으로 튕긴다(§S4).
     */
    suspend fun awaitAuthReady()

    suspend fun currentAccount(): UserAccount?

    /**
     * ID 토큰 강제 갱신. custom claim은 최대 1시간 캐시된다(R11).
     *
     * 학원 승인·역할 부여 직후 이것을 부르지 않으면 반드시 "승인했는데 권한이 없다"가 된다.
     */
    suspend fun forceRefreshToken()

    suspend fun memberships(userId: String): List<AcademyMembership>
    suspend fun logout()

    /** 현재 로그인한 사용자 자신의 표시 이름만 변경한다(`edubridge-updateMyProfile`). */
    suspend fun updateMyProfile(name: String): ProfileUpdateResult = ProfileUpdateResult(
        failure = CommandFailure.INTERNAL,
        errorMessage = "이 플랫폼에서는 프로필 수정을 아직 지원하지 않아요.",
    )

    /** 서버가 계산한 삭제 가능 여부와 현재 유예 상태를 가져온다. */
    suspend fun accountDeletionStatus(): AccountDeletionState = unsupportedAccountDeletionState()

    /** 서버에 7일 유예 삭제를 요청한다. reason은 선택 입력이며 null이면 payload에서 생략한다. */
    suspend fun requestAccountDeletion(reason: String?): AccountDeletionState = unsupportedAccountDeletionState()

    /** 유예 중인 삭제 요청만 취소한다. */
    suspend fun cancelAccountDeletion(): AccountDeletionState = unsupportedAccountDeletionState()
}

private fun unsupportedAccountDeletionState(): AccountDeletionState = AccountDeletionState(
    failure = CommandFailure.INTERNAL,
    errorMessage = "이 플랫폼에서는 계정 삭제를 아직 지원하지 않아요.",
)
